import { useCallback, useMemo, useState } from "react";
import {
  Stack,
  Group,
  Title,
  Button,
  Loader,
  Text,
  Table,
  Center,
  Alert,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconPlus, IconAlertCircle, IconSend } from "@tabler/icons-react";
import {
  PortalAccessGrant,
  PortalSubjectType,
} from "../../../services/admin/portalAccess.service";
import { usePortalAccessGrantsForSubject } from "../../../hooks/admin/usePortalAccess";
import { AddPortalGrantModal, PrimaryDefaults } from "./AddPortalGrantModal";
import { EditPortalGrantModal } from "./EditPortalGrantModal";
import { PortalGrantRow } from "./PortalGrantRow";

interface PortalAccessSectionProps {
  subjectType: PortalSubjectType;
  subjectId: string;
  subjectName?: string;
  subjectEmail?: string | null;
  subjectFirstName?: string | null;
  subjectLastName?: string | null;
  title?: string;
}

export function PortalAccessSection({
  subjectType,
  subjectId,
  subjectName,
  subjectEmail,
  subjectFirstName,
  subjectLastName,
  title,
}: PortalAccessSectionProps) {
  const [addOpened, { open: openAdd, close: closeAdd }] = useDisclosure(false);
  const [editingGrant, setEditingGrant] = useState<PortalAccessGrant | null>(
    null
  );

  const {
    data: grants,
    isLoading,
    isError,
  } = usePortalAccessGrantsForSubject(subjectType, subjectId);

  const hasPrimary = useMemo(
    () => Boolean(grants?.some((g) => g.state !== "revoked")),
    [grants]
  );

  const primaryDefaults = useMemo<PrimaryDefaults>(() => {
    if (subjectType === "physician") {
      return {
        email: subjectEmail ?? "",
        firstName: subjectFirstName ?? "",
        lastName: subjectLastName ?? "",
        role: "Self",
      };
    }
    return {
      email: subjectEmail ?? "",
      firstName: "",
      lastName: "",
      role: "Contact Person",
    };
  }, [subjectType, subjectEmail, subjectFirstName, subjectLastName]);

  const handleEdit = useCallback((grant: PortalAccessGrant) => {
    setEditingGrant(grant);
  }, []);

  const handleCloseEdit = useCallback(() => {
    setEditingGrant(null);
  }, []);

  const sectionTitle = useMemo(
    () =>
      title ??
      (subjectType === "physician" ? "Portal Access" : "Portal Contacts"),
    [title, subjectType]
  );

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        <Title order={5}>{sectionTitle}</Title>
        <Button
          size="xs"
          variant={hasPrimary ? "light" : "filled"}
          leftSection={
            hasPrimary ? <IconPlus size={14} /> : <IconSend size={14} />
          }
          onClick={openAdd}
        >
          {hasPrimary ? "Add Contact" : "Invite"}
        </Button>
      </Group>

      {isLoading ? (
        <Center py="md">
          <Loader size="sm" />
        </Center>
      ) : isError ? (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          variant="light"
          title="Couldn't load portal access"
          role="alert"
        >
          The portal access list failed to load. Please refresh and try again.
        </Alert>
      ) : !grants || grants.length === 0 ? (
        <Text size="sm" c="dimmed" py="xs">
          No portal access yet. Invite the primary contact to get started.
        </Text>
      ) : (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Email</Table.Th>
              <Table.Th>Role</Table.Th>
              <Table.Th>State</Table.Th>
              <Table.Th>Notifications</Table.Th>
              <Table.Th>Last Login</Table.Th>
              <Table.Th style={{ width: 60 }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {grants.map((grant) => (
              <PortalGrantRow
                key={grant.id}
                grant={grant}
                onEdit={handleEdit}
              />
            ))}
          </Table.Tbody>
        </Table>
      )}

      <AddPortalGrantModal
        opened={addOpened}
        onClose={closeAdd}
        subjectType={subjectType}
        subjectId={subjectId}
        subjectName={subjectName}
        mode={hasPrimary ? "additional" : "primary"}
        primaryDefaults={primaryDefaults}
      />
      <EditPortalGrantModal
        opened={editingGrant !== null}
        onClose={handleCloseEdit}
        grant={editingGrant}
      />
    </Stack>
  );
}
